import React from "react";
import { Eyebrow } from "../components/ui.js";

/**
 * What a step produced, read on its own terms.
 *
 * The history could say a step ran, how long it took and what it cost, but not
 * what came out of it — which is the question anyone actually opens this page
 * with. Raw JSON would answer it and read like a crash dump; each artifact gets
 * the shape it deserves instead, and anything unrecognised falls back to JSON
 * rather than showing nothing.
 */

const field = (value: unknown, key: string): unknown =>
  value && typeof value === "object" ? (value as Record<string, unknown>)[key] : undefined;

const isArray = (value: unknown): value is Record<string, unknown>[] => Array.isArray(value);

const time = (seconds: unknown) => (typeof seconds === "number" ? `${seconds.toFixed(2)}s` : "");

const Row: React.FC<{ at?: string; children: React.ReactNode }> = ({ at, children }) => (
  <div className="flex gap-3 border-b border-[var(--edge)] py-1.5 last:border-b-0">
    {at ? <span className="tnum w-[92px] shrink-0 text-[11px] text-ink-400">{at}</span> : null}
    <div className="min-w-0 flex-1 text-[12px] leading-relaxed text-ink-200">{children}</div>
  </div>
);

/** Long lists are truncated: the point is to see what it looks like, not to read all of it. */
const LIMIT = 40;

const More: React.FC<{ shown: number; total: number }> = ({ shown, total }) =>
  total > shown ? <p className="pt-2 text-[11px] text-ink-400">…and {total - shown} more.</p> : null;

const Raw: React.FC<{ value: unknown }> = ({ value }) => (
  <pre className="max-h-[420px] overflow-auto rounded-[6px] bg-bay-850 p-3 font-mono text-[11px] leading-relaxed text-ink-300">
    {JSON.stringify(value, null, 2)}
  </pre>
);

export const StepOutputView: React.FC<{ step: string; output: unknown }> = ({ step, output }) => {
  if (output === null || output === undefined) return null;

  // Transcript — what was said, and when.
  const segments = field(output, "segments");
  if (step === "transcribed" && isArray(segments)) {
    return (
      <Section title="What was said" count={segments.length}>
        {segments.slice(0, LIMIT).map((segment, index) => (
          <Row key={index} at={time(segment.start)}>
            {String(segment.text ?? "")}
          </Row>
        ))}
        <More shown={LIMIT} total={segments.length} />
      </Section>
    );
  }

  // The script, plus the model's own reasoning where it gave any.
  const scenes = field(output, "scenes");
  if (step === "script-generated" && isArray(scenes)) {
    const edlScenes = field(field(output, "edl"), "scenes");
    const reasons = isArray(edlScenes) ? edlScenes : [];
    return (
      <Section title="The narration it wrote" count={scenes.length}>
        {scenes.slice(0, LIMIT).map((scene, index) => {
          const reason = reasons[index];
          return (
            <Row key={index} at={`Scene ${index + 1}`}>
              <span className="block text-ink-100">{String(scene.script ?? "")}</span>
              {reason?.reason ? (
                <span className="mt-1 block text-[11px] italic text-ink-400">
                  {String(reason.reason)}
                  {typeof reason.confidence === "number" ? ` · ${Math.round(reason.confidence * 100)}% sure` : ""}
                </span>
              ) : null}
            </Row>
          );
        })}
        <More shown={LIMIT} total={scenes.length} />
      </Section>
    );
  }

  // What the vision model saw on screen.
  if (step === "visuals-analysed" && isArray(scenes)) {
    return (
      <Section title="What the model saw" count={scenes.length}>
        {scenes.slice(0, LIMIT).map((scene, index) => (
          <Row key={index} at={`${time(scene.start)}–${time(scene.end)}`}>
            <span className="block text-ink-100">{String(scene.description ?? "")}</span>
            {isArray(scene.actions) || (Array.isArray(scene.actions) && scene.actions.length > 0) ? (
              <span className="mt-1 block text-[11px] text-ink-400">{(scene.actions as string[]).join(" · ")}</span>
            ) : null}
          </Row>
        ))}
        <More shown={LIMIT} total={scenes.length} />
      </Section>
    );
  }

  // Interactions the detector found.
  const actions = field(output, "actions");
  if (step === "actions-detected" && isArray(actions)) {
    return (
      <Section title="What it found happening" count={actions.length}>
        {actions.slice(0, LIMIT).map((action, index) => (
          <Row key={index} at={time(action.timestamp)}>
            <span className="text-ink-100">{String(action.type ?? "")}</span>
            {action.label ? <span className="text-ink-300"> — {String(action.label)}</span> : null}
            <span className="text-[11px] text-ink-400"> · {String(action.source ?? "")}</span>
          </Row>
        ))}
        <More shown={LIMIT} total={actions.length} />
      </Section>
    );
  }

  // What survived the cut.
  const keeps = field(output, "keeps");
  if (step === "cut-planned" && isArray(keeps)) {
    return (
      <Section title="What it kept" count={keeps.length}>
        {keeps.slice(0, LIMIT).map((keep, index) => (
          <Row key={index} at={`Take ${index + 1}`}>
            {time(keep.start)} – {time(keep.end)}
          </Row>
        ))}
        <More shown={LIMIT} total={keeps.length} />
      </Section>
    );
  }

  // Timelines are stats, not a document; raw is the honest rendering.
  return (
    <Section title="What it produced">
      <Raw value={output} />
    </Section>
  );
};

const Section: React.FC<{ title: string; count?: number; children: React.ReactNode }> = ({
  title,
  count,
  children,
}) => (
  <section className="panel p-5">
    <Eyebrow>
      {title}
      {count === undefined ? "" : ` · ${count}`}
    </Eyebrow>
    <div className="mt-3">{children}</div>
  </section>
);
